/**
 * pages/knowledge/qr.js — QR УНШУУЛАХАД НЭЭГДЭХ ДЭЛГЭЦ
 *
 * Зам: /knowledge/qr/:code
 *
 * Эх сурвалж: «process flow HSD-2026.vsdx» page4 «МИНУТЫН МЭДЛЭГ ОЛГОХ
 * ПРОЦЕСС» → «Суралцах суваг сонгох»:
 *   «Багаж хэрэгсэл, машин техник дээр QR код байршуулна. Ажилтан кодыг
 *    уншуулахад тухайн техникийг ашиглах 1 минутын "Санамж видео" гарч ирнэ.»
 *
 * Онцлог: ажилтан утсаараа уншуулж, ТАЛБАЙ ДЭЭР зогсоод үзнэ. Тиймээс
 * дэлгэц нь хамгийн энгийн — нэг бичлэг, нэг мессеж. Цэс, шүүлт байхгүй.
 * Үзэлт нь `source='qr'` гэж бүртгэгдэнэ.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Card, Spin, Result, Button, Collapse, Tag, Space } from "antd";

import { knowledgeApi } from "src/api/knowledgeApi";
import { getAuth } from "src/api/request";
import { KNOWLEDGE_DARK } from "src/pages/knowledge/theme";

/**
 * SSO рүү явахын өмнө ЭНЭ QR-ын замыг сануулна.
 *
 * ⚠ QR хуудас нь нэвтрэлтгүй тул `userContext`-ийн ерөнхий шилжүүлэг энд
 *   ажиллахгүй. Тиймээс буцах замыг өөрөө хадгална — эс бөгөөс ажилтан
 *   нэвтэрсний дараа нүүр хуудсанд гарч, наалтаа дахин уншуулах болно.
 */
function loginAndReturn() {
  try {
    sessionStorage.setItem(
      "post_login_path",
      window.location.pathname + (window.location.search || ""),
    );
  } catch (e) {
    /* хувийн горим */
  }
  getAuth().then((res) => {
    const url = new URL(res.data.url);
    url.searchParams.set("redirect_uri", `${window.location.origin}/callback`);
    window.location.replace(url.href);
  });
}

/**
 * Тухайн тоног төхөөрөмж дээр мөрдөх баримтын жагсаалт.
 *
 * ⚠ «Агуулга хавсрагдаагүй» тэмдэглэгээ нь ЗААВАЛ харагдана. Лавлах нь
 *   HRT-ээс дугаар + нэр төдийхнөөр хөрвүүлэгдсэн тул ихэнх зааврын
 *   бичвэр систем дотор БАЙХГҮЙ. Үүнийг нуувал ажилтан жагсаалт хараад
 *   «бүгд системд бий» гэж ойлгож, бодит зааврыг уншихгүй өнгөрнө —
 *   аюулгүй байдлын системд энэ нь ноцтой алдаа.
 */
function DocRow({ d }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        padding: "7px 0",
        borderBottom: "1px solid #F2F4F7",
      }}
    >
      <Tag
        style={{
          marginRight: 0,
          fontFamily: "monospace",
          fontSize: 11,
          minWidth: 44,
          textAlign: "center",
        }}
      >
        {d.no || "—"}
      </Tag>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, lineHeight: 1.45, color: "#1F2430" }}>
          {d.description || "(нэргүй)"}
        </div>
        {d.has_content ? null : (
          <div style={{ fontSize: 11, color: "#D97706", marginTop: 2 }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }} />
            Агуулга хавсрагдаагүй — эх баримтыг ХАБЭА-аас авна
          </div>
        )}
      </div>
      {d.file_url ? (
        <a href={d.file_url} target="_blank" rel="noreferrer">
          <i className="fas fa-file-pdf" style={{ color: "#DC2626" }} />
        </a>
      ) : null}
    </div>
  );
}

function DeviceDocs({ docs }) {
  if (!docs || !docs.total) return null;
  const { instructions = [], cards = [], with_content: withContent, total } = docs;

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#1F2430",
          marginBottom: 6,
        }}
      >
        <i className="fas fa-tools" style={{ marginRight: 7 }} />
        {docs.device?.name || "Тоног төхөөрөмж"} дээр мөрдөх баримт ({total})
      </div>

      {!withContent ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="Эдгээр баримтын бичвэр систем дотор одоогоор алга"
          description={
            "Аль заавар хамаарахыг эндээс харна. Харин заавар дотор ЮУ " +
            "гэж заасныг эх баримтаас нь үзнэ."
          }
        />
      ) : null}

      {instructions.length ? (
        <>
          <div style={{ fontSize: 12, color: "#8A94A6", marginTop: 8 }}>
            ХАБ ЗААВАР — {instructions.length}
          </div>
          {instructions.map((d) => (
            <DocRow key={"i" + d.id} d={d} />
          ))}
        </>
      ) : null}

      {cards.length ? (
        // Карт нь олон байдаг (жишээ: ШИГШҮҮР дээр 23) тул хумьж
        // харуулна — ажилтны дэлгэц эхлээд ЗААВРААР эхлэх ёстой.
        <Collapse
          ghost
          style={{ marginTop: 6 }}
          defaultActiveKey={cards.length <= 5 ? ["c"] : []}
        >
          <Collapse.Panel
            key="c"
            header={
              <span style={{ fontSize: 12, color: "#8A94A6" }}>
                ТЕХНОЛОГИЙН КАРТ — {cards.length}
              </span>
            }
          >
            {cards.map((d) => (
              <DocRow key={"c" + d.id} d={d} />
            ))}
          </Collapse.Panel>
        </Collapse>
      ) : null}
    </div>
  );
}

export default function KnowledgeQrView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // ⚠ ХОЁР ЗАМ. Нэвтэрсэн хүн бүрэн агуулга (бичлэг, файл) авна; нэвтрээгүй
  //   ажилтан нийтийн хаягаар ЗӨВХӨН зааврын гарчгийг авна. Талбар дээр
  //   зогсоод нэвтрэхийг хүлээхгүйгээр «юу мөрдөх ёстойгоо» шууд харах нь
  //   зорилго — аюулгүй байдлын мэдээлэл саадгүй хүрэх ёстой.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const authed = !!localStorage.getItem("token");
    const call = authed
      ? knowledgeApi.qrScan(code)
      : knowledgeApi.qrScanPublic(code);
    call
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive)
          setErr(
            e?.response?.data?.message || "QR кодыг уншиж чадсангүй",
          );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [code]);

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" tip="Санамж бичлэгийг ачаалж байна..." />
      </div>
    );
  }

  const authed = !!localStorage.getItem("token");

  if (err) {
    return (
      <Result
        status="warning"
        title="Олдсонгүй"
        subTitle={err}
        extra={
          // Нэвтрээгүй хүнд «минутын мэдлэг» рүү явуулах нь утгагүй —
          // тэр хуудас бас нэвтрэлт шаардана. Нэвтрэхийг санал болгоно.
          authed ? (
            <Button type="primary" onClick={() => navigate("/knowledge/my")}>
              Минутын мэдлэг рүү очих
            </Button>
          ) : (
            <Button type="primary" onClick={loginAndReturn}>
              Нэвтэрч үзэх
            </Button>
          )
        }
      />
    );
  }

  const { qr, content, topic, docs } = data || {};
  const hasDocs = !!(docs && docs.total);
  const isPublic = !!data?.public;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
      <Card
        bodyStyle={{ padding: 16 }}
        style={{ borderRadius: 14 }}
        title={
          <div>
            {/* ⚠ Бичлэггүй, зөвхөн заавартай QR дээр `qr.name` нь ихэвчлэн
                «шинэ» гэх мэт утгагүй нэр байдаг. Тиймээс төхөөрөмжийн
                нэрийг эрэмбэлж түрүүлүүлнэ — ажилтан юуны дэргэд зогсож
                байгаагаа эхлээд танина. */}
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {content?.title ||
                docs?.device?.name ||
                qr?.device_name ||
                qr?.name}
            </div>
            <div style={{ fontSize: 12, color: "#8A94A6", fontWeight: 400 }}>
              {content?.title ? qr?.device_name || qr?.name : qr?.name}
              {qr?.location ? ` · ${qr.location}` : ""}
            </div>
          </div>
        }
        extra={
          topic?.category_name ? (
            <Tag color={topic.category_code === "EA" ? "blue" : "green"}>
              {topic.category_name}
            </Tag>
          ) : null
        }
      >
        {content?.content_type === "video" && content?.file_url ? (
          <video
            src={content.file_url}
            controls
            autoPlay
            playsInline
            controlsList="nodownload"
            style={{
              width: "100%",
              maxHeight: "62vh",
              display: "block",
              objectFit: "contain",
              borderRadius: 10,
              background: "#000",
            }}
          />
        ) : content?.file_url ? (
          <img
            src={content.file_url}
            alt={content.title}
            style={{
              width: "100%",
              maxHeight: "62vh",
              display: "block",
              objectFit: "contain",
              borderRadius: 10,
            }}
          />
        ) : hasDocs ? null : (
          // ⚠ Баримт байвал «хоосон» гэж хэлэхгүй — доор харуулах зүйл бий
          <Result status="info" title="Энэ QR-д бичлэг холбогдоогүй байна" />
        )}

        {content?.description ? (
          <p style={{ marginTop: 12, color: "#5F5E5A", fontSize: 13.5 }}>
            {content.description}
          </p>
        ) : null}

        <DeviceDocs docs={docs} />

        {isPublic ? (
          // ⚠ Нэвтрээгүй ажилтанд ЮУГ НЬ ХАРААГҮЙГЭЭ мэдэгдэнэ. Хагас
          //   мэдээллийг бүтэн гэж ойлгуулах нь аюулгүй байдлын системд
          //   болохгүй.
          <div
            style={{
              marginTop: 14,
              padding: "10px 12px",
              background: "#F1F5F9",
              borderRadius: 8,
              fontSize: 12.5,
              color: "#475569",
            }}
          >
            <i className="fas fa-user-clock" style={{ marginRight: 7 }} />
            Та нэвтрээгүй байна — <b>үзсэн тэмдэглэл хөтлөгдөхгүй</b>.
            Сургалтын бүртгэлд тоологдох бол нэвтэрнэ үү.
            <div style={{ marginTop: 8 }}>
              <Button type="primary" size="small" onClick={loginAndReturn}>
                Нэвтрэх
              </Button>
            </div>
          </div>
        ) : (
          <Space wrap style={{ marginTop: 14 }}>
            <Button type="primary" onClick={() => navigate("/knowledge/my")}>
              Өнөөдрийн мэдлэг
            </Button>
            {topic?.id ? (
              <Button onClick={() => navigate("/knowledge/register")}>
                Сэдвийн дэлгэрэнгүй
              </Button>
            ) : null}
          </Space>
        )}

        <div
          style={{
            marginTop: 14,
            paddingTop: 10,
            borderTop: "1px solid #EEF0F3",
            fontSize: 11.5,
            color: "#9AA3B0",
          }}
        >
          QR: <b style={{ color: KNOWLEDGE_DARK }}>{qr?.code}</b>
          {qr?.scan_count ? ` · ${qr.scan_count} удаа уншуулсан` : ""}
        </div>
      </Card>
    </div>
  );
}
